"use client";

import React from "react";
import { 
  Calendar, 
  User, 
  Clock, 
  ArrowRight, 
  Share2, 
  Tag, 
  Facebook, 
  Twitter, 
  Linkedin, 
  ChevronLeft 
} from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

/**
 *  相關推薦文章列表
 */
function RelatedPostsGrid() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-[400px] rounded-xl bg-muted/20 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} className="flex flex-col overflow-hidden border-border/50 bg-card shadow-sm hover:shadow-md transition-shadow">
          <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground text-sm">
                暫無圖片 / NO IMAGE
              </div>
            )}
            <div className="absolute top-4 left-4">
              <span className="inline-flex items-center rounded-full bg-primary/90 px-2.5 py-0.5 text-xs font-medium text-primary-foreground backdrop-blur-sm">
                {post.categories?.[0]?.name || "地板百科 / DISCOVERY"}
              </span>
            </div>
          </div>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "近期文章"}</span>
            </div>
            <CardTitle className="font-sans text-lg line-clamp-2 leading-tight">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow pb-4">
            <div 
              className="text-sm text-muted-foreground line-clamp-3"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full group border-primary/20 hover:border-primary hover:bg-primary/5 text-primary">
                閱讀全文 / READ MORE
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

/**
 *  推薦閱讀包裹層
 */
function RelatedPostsWrapper({ currentPostId, categoryId }: { currentPostId: string; categoryId?: number }) {
  const queryParams: any = {
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [currentPostId],
    orderby: "date",
    order: "DESC",
  };

  if (categoryId) queryParams.cat = categoryId;

  const wp_query = new WP_Query(queryParams);

  return (
    <div className="mt-16 md:mt-24">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl md:text-3xl font-bold font-sans text-foreground text-left">推薦閱讀 / RELATED ARTICLES</h3>
        <Link to="/knowledge">
          <Button variant="ghost" className="hidden md:flex">查看更多文章 / VIEW ALL <ArrowRight className="ml-2 h-4 w-4" /></Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsGrid />
      </PostsProvider>
    </div>
  );
}

/**
 *  文章主要渲染層
 */
function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12 space-y-8 animate-pulse">
        <div className="h-8 w-2/3 bg-muted rounded" />
        <div className="h-96 w-full bg-muted rounded-xl" />
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-24 text-center">
        <h2 className="text-2xl font-bold mb-4 italic">找不到內容或載入失敗</h2>
        <Button onClick={() => window.history.back()}>返回上一頁 / BACK</Button>
      </div>
    );
  }

  const post = posts[0];
  const category = post.categories?.[0];
  const authorName = post.author?.name || "亨特團隊 / Hent's Team";
  const publishDate = post.date ? new Date(post.date).toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric' }) : "";

  return (
    <article className="container mx-auto max-w-4xl px-4">
      
      {/* ---  修正版：原生返回按鈕 (無 Next 套件依賴) --- */}
      <div className="mb-10 text-left">
        <button
          onClick={() => {
            if (typeof window !== "undefined") {
              window.history.back();
            }
          }}
          className="group inline-flex items-center gap-3 text-muted-foreground hover:text-primary transition-all duration-300"
        >
          <div className="flex items-center justify-center size-10 rounded-full border border-border group-hover:border-primary group-hover:bg-primary/5 transition-all">
            <ChevronLeft className="size-5 transition-transform group-hover:-translate-x-1" />
          </div>
          <div className="flex flex-col items-start">
            <span className="text-[10px] tracking-[0.2em] font-bold uppercase opacity-60 leading-none mb-1">Back to List</span>
            <span className="text-sm font-bold tracking-widest uppercase leading-none">回到文章列表 / BACK</span>
          </div>
        </button>
      </div>

      <header className="mb-10 text-left">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {category && (
            <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary ring-1 ring-inset ring-primary/20">
              {category.name}
            </span>
          )}
          <span className="flex items-center text-sm text-muted-foreground">
            <Calendar className="mr-1.5 h-4 w-4" />
            {publishDate}
          </span>
        </div>

        <h1 className="font-sans text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6 leading-tight">
          {post.title}
        </h1>

        <div className="flex flex-col md:flex-row items-center justify-between gap-6 border-y border-border py-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12 border-2 border-background shadow-sm">
              <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={authorName} />
              <AvatarFallback>{authorName.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground">{authorName}</p>
              <p className="text-xs text-muted-foreground">專欄作者 / AUTHOR</p>
            </div>
          </div>

       <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground mr-2">分享文章:</span>
            
            {/*  Facebook 分享功能 */}
            <Button 
              variant="outline" 
              size="icon" 
              className="h-9 w-9 rounded-full hover:bg-[#1877f2]/10 hover:text-[#1877f2] transition-colors"
              onClick={() => {
                if (typeof window !== "undefined") {
                  const url = encodeURIComponent(window.location.href);
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank", "width=600,height=400");
                }
              }}
            >
              <Facebook className="h-4 w-4" />
            </Button>

            {/*  X (原 Twitter) 分享功能 */}
            <Button 
              variant="outline" 
              size="icon" 
              className="h-9 w-9 rounded-full hover:bg-black/10 hover:text-black dark:hover:text-white transition-colors"
              onClick={() => {
                if (typeof window !== "undefined") {
                  const url = encodeURIComponent(window.location.href);
                  const text = encodeURIComponent(post.title || "");
                  window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, "_blank", "width=600,height=300");
                }
              }}
            >
              <Twitter className="h-4 w-4" />
            </Button>

            {/*  行動端原生分享 / 電腦端一鍵複製連結功能 */}
            <Button 
              variant="outline" 
              size="icon" 
              className="h-9 w-9 rounded-full hover:bg-primary/10 hover:text-primary transition-colors"
              onClick={async () => {
                if (typeof window !== "undefined" && typeof navigator !== "undefined") {
                  // 如果是手機（支援原生 Web Share API）就直接喚起系統分享選單 (LINE, FB MESSENGER 等)
                  if (navigator.share) {
                    try {
                      await navigator.share({
                        title: post.title,
                        url: window.location.href,
                      });
                    } catch (err) {
                      console.log("取消分享或分享失敗:", err);
                    }
                  } else {
                    // 如果是電腦端，自動複製網址到剪貼簿，並彈出簡單提示
                    try {
                      await navigator.clipboard.writeText(window.location.href);
                      alert("連結已複製到剪貼簿！可直接貼上分享。");
                    } catch (err) {
                      console.error("無法複製網址:", err);
                    }
                  }
                }
              }}
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-2xl shadow-lg">
          <img src={post.featuredImage} alt={post.title} className="w-full h-auto object-cover max-h-[600px]" />
        </div>
      )}

      <div className="prose prose-lg prose-slate dark:prose-invert max-w-none mb-16 text-left">
        <div dangerouslySetInnerHTML={{ __html: post.content || "" }} />
      </div>

      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-12">
          <Tag className="h-4 w-4 text-primary" />
          {post.tags.map((tag) => (
            <span key={tag.id} className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground cursor-default">
              #{tag.name}
            </span>
          ))}
        </div>
      )}

      <div className="rounded-xl bg-muted/30 p-8 mb-16 border border-border text-left">
        <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
          <Avatar className="h-20 w-20 border-4 border-background shadow-md">
            <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={authorName} />
            <AvatarFallback className="text-xl">{authorName.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h3 className="text-xl font-bold font-sans mb-2">關於 {authorName}</h3>
            <p className="text-muted-foreground mb-4">
              亨特地板工程專家，專注於提供高品質的木地板解決方案與專業施工服務。
            </p>
          </div>
        </div>
      </div>

      <Separator />
      <RelatedPostsWrapper currentPostId={post.id} categoryId={category?.id} />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams = (window as any).wvcClient?.getCurrentWPQueryParams() || { post_type: "post" };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background py-12 md:py-24 text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}